import { format } from 'date-fns/format';
import { getYear } from 'date-fns/getYear';
import { parse } from 'date-fns/parse';
import { formatMonthYearFr } from '@bloomays-lib/utils.shared';
import { TAM_ANNUAL_MARGIN_OBJECTIVES } from '@bloomays-lib/types.shared';
import {
  AnnualPrimeTableRow,
  AnnualTamPrimeAggregate,
  MonthlyPrimeNavigationTarget,
  MonthlyTamPrimeAggregate,
  MonthlyTamPrimeQueryResult,
  ValidatedTamPrimeAggregate,
} from '../types/primes';

import { formatDateToMonthYear, splitPrimeMonthLabel } from './monthlyPrime.utils';

/**
 * Affichage du KPI objectif annuel (TEC-2816).
 * Passer à `true` lorsque les données de marge/objectif sont fiables en production.
 */
export const ANNUAL_GOAL_COMPLETION_UI_ENABLED = false;

export type AnnualCurrentMonthHeaderState = {
  monthLabel: string;
  monthName: string;
  year: string;
  status: 'validated' | 'pending';
  totalPrime: number;
  regularisation: number;
  bonus: number;
};

export type AnnualGoalHeaderState = {
  cumulativeMargin: number;
  targetAmount: number;
  percent: number;
  remainingAmount: number;
  progressBarPercent: number;
};

const monthYearSortKey = (monthYear: string): number => parse(monthYear, 'MM/yyyy', new Date()).getTime();

export const isCurrentYear = (year: string): boolean => year === format(new Date(), 'yyyy');

export const getAnnualCurrentMonthHeaderState = (
  aggregate: Pick<AnnualTamPrimeAggregate, 'validatedPrimes' | 'pendingPrimes'>,
): AnnualCurrentMonthHeaderState | null => {
  const currentMonthYear = format(new Date(), 'MM/yyyy');
  const currentReferenceDate = parse(currentMonthYear, 'MM/yyyy', new Date());

  if (aggregate.pendingPrimes !== null) {
    const monthLabel = formatMonthYearFr(currentReferenceDate);
    const { monthName, year } = splitPrimeMonthLabel(monthLabel);

    return {
      monthLabel,
      monthName,
      year,
      status: 'pending',
      totalPrime: aggregate.pendingPrimes.totalPrime,
      regularisation: aggregate.pendingPrimes.regularisation,
      bonus: aggregate.pendingPrimes.bonus,
    };
  }

  const validatedCurrentMonth = aggregate.validatedPrimes.find(
    (validatedPrime) => validatedPrime.monthYear === currentMonthYear,
  );

  if (!validatedCurrentMonth) {
    return null;
  }

  const monthLabel = formatMonthYearFr(currentReferenceDate);
  const { monthName, year } = splitPrimeMonthLabel(monthLabel);

  return {
    monthLabel,
    monthName,
    year,
    status: 'validated',
    totalPrime: validatedCurrentMonth.totalPrime,
    regularisation: validatedCurrentMonth.regularisation,
    bonus: validatedCurrentMonth.bonus,
  };
};

const toSummaryFields = (
  aggregate: Pick<
    MonthlyTamPrimeAggregate,
    | 'bonus'
    | 'regularisation'
    | 'totalFreelanceMarge'
    | 'totalBillingMarge'
    | 'totalMarge'
    | 'totalPrime'
    | 'totalDeals'
    | 'totalMissions'
  >,
): Pick<
  AnnualPrimeTableRow,
  | 'bonus'
  | 'regularisation'
  | 'totalFreelanceMarge'
  | 'totalBillingMarge'
  | 'totalMarge'
  | 'totalPrime'
  | 'totalDeals'
  | 'totalMissions'
> => ({
  bonus: aggregate.bonus,
  regularisation: aggregate.regularisation,
  totalFreelanceMarge: aggregate.totalFreelanceMarge,
  totalBillingMarge: aggregate.totalBillingMarge,
  totalMarge: aggregate.totalMarge,
  totalPrime: aggregate.totalPrime,
  totalDeals: aggregate.totalDeals,
  totalMissions: aggregate.totalMissions,
});

export const mapAnnualTamPrimeToTableRows = (aggregate: AnnualTamPrimeAggregate): AnnualPrimeTableRow[] => {
  const rows: AnnualPrimeTableRow[] = aggregate.validatedPrimes.map((validatedPrime) => ({
    rowId: `validated-${validatedPrime.id}`,
    monthYear: validatedPrime.monthYear,
    status: 'validated',
    ...toSummaryFields(validatedPrime),
  }));

  if (aggregate.pendingPrimes !== null) {
    const pendingMonthYear = format(new Date(), 'MM/yyyy');
    rows.push({
      rowId: `pending-${pendingMonthYear}`,
      monthYear: pendingMonthYear,
      status: 'pending',
      ...toSummaryFields(aggregate.pendingPrimes),
    });
  }

  return rows.sort((left, right) => monthYearSortKey(left.monthYear) - monthYearSortKey(right.monthYear));
};

export const getAnnualGoalHeaderState = (aggregate: AnnualTamPrimeAggregate): AnnualGoalHeaderState => {
  const validatedTotalMargin = aggregate.validatedPrimes.reduce(
    (total, validatedPrime) => total + validatedPrime.totalMarge,
    0,
  );
  const pendingTotalMargin = aggregate.pendingPrimes?.totalMarge ?? 0;
  const cumulativeMargin = validatedTotalMargin + pendingTotalMargin;
  const targetAmount = aggregate.tamSeniority === 'Director' ? 0 : TAM_ANNUAL_MARGIN_OBJECTIVES[aggregate.tamSeniority];
  const percent = aggregate.goalCompletion;
  return {
    cumulativeMargin,
    targetAmount,
    percent,
    remainingAmount: Math.max(0, targetAmount - cumulativeMargin),
    progressBarPercent: Math.min(percent, 100),
  };
};

export const yearToReferenceDate = (year: string): Date => new Date(getYear(parse(year, 'yyyy', new Date())), 0, 1);

export const referenceDateToYear = (date: Date): string => format(date, 'yyyy');

export const buildCLevelAnnualDetailPath = (tamEmail: string, year: string): string => {
  const search = new URLSearchParams({ tam: tamEmail, year }).toString();
  return `/primes/dashboard/details?${search}`;
};

export const formatMonthYearToUrlParam = (monthYear: string): string =>
  formatDateToMonthYear(parse(monthYear, 'MM/yyyy', new Date()));

const toValidatedTamPrimeAggregate = (
  validatedPrime: AnnualTamPrimeAggregate['validatedPrimes'][number],
): ValidatedTamPrimeAggregate => ({
  __typename: 'ValidatedTamPrime',
  id: validatedPrime.id,
  monthYear: validatedPrime.monthYear,
  validationDate: validatedPrime.validationDate,
  primes: validatedPrime.primes,
  bonus: validatedPrime.bonus,
  regularisation: validatedPrime.regularisation,
  totalFreelanceMarge: validatedPrime.totalFreelanceMarge,
  totalBillingMarge: validatedPrime.totalBillingMarge,
  totalMarge: validatedPrime.totalMarge,
  totalPrime: validatedPrime.totalPrime,
  totalDeals: validatedPrime.totalDeals,
  totalMissions: validatedPrime.totalMissions,
});

export const mapAnnualRowToMonthlyTamPrimeQueryResult = (
  aggregate: AnnualTamPrimeAggregate,
  row: AnnualPrimeTableRow,
): MonthlyTamPrimeQueryResult => {
  if (row.status === 'pending') {
    if (aggregate.pendingPrimes === null) {
      throw new Error(`mapAnnualRowToMonthlyTamPrimeQueryResult: no pending prime for row ${row.rowId}`);
    }
    return aggregate.pendingPrimes;
  }

  const validatedPrime = aggregate.validatedPrimes.find((candidate) => candidate.monthYear === row.monthYear);
  if (!validatedPrime) {
    throw new Error(`mapAnnualRowToMonthlyTamPrimeQueryResult: no validated prime for row ${row.rowId}`);
  }

  return toValidatedTamPrimeAggregate(validatedPrime);
};

export type BuildMonthlyPrimeNavigationTargetParams = {
  aggregate: AnnualTamPrimeAggregate;
  row: AnnualPrimeTableRow;
  tamEmail: string;
  isTamPreview: boolean;
};

export const buildMonthlyPrimeNavigationTarget = ({
  aggregate,
  row,
  tamEmail,
  isTamPreview,
}: BuildMonthlyPrimeNavigationTargetParams): MonthlyPrimeNavigationTarget => {
  const mmYyyy = formatMonthYearToUrlParam(row.monthYear);
  const trimmedTamEmail = tamEmail.trim();
  const search = isTamPreview ? `?${new URLSearchParams({ tam: trimmedTamEmail }).toString()}` : '';

  return {
    pathname: `/primes/${mmYyyy}`,
    search,
    state: {
      preloadedMonthlyTamPrime: mapAnnualRowToMonthlyTamPrimeQueryResult(aggregate, row),
    },
  };
};
